import { useRef, useState } from "react";
import { ImageIcon, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useDeleteVehicleImage,
  useUploadVehicleImage,
  type VehicleImage,
} from "@/hooks/useVehicles";

/** Photo tiles in the same responsive sizes as the equipment gallery. */
export function VehiclePhotos({
  vehicleId,
  images,
}: {
  vehicleId: string;
  images: VehicleImage[];
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const upload = useUploadVehicleImage();
  const remove = useDeleteVehicleImage();

  const handleFiles = (files: FileList | null) => {
    const nextOrder = images.reduce(
      (max, image) => Math.max(max, image.displayOrder + 1),
      0,
    );
    Array.from(files ?? [])
      .filter((file) => file.type.startsWith("image/"))
      .forEach((file, index) =>
        upload.mutate({ vehicleId, file, displayOrder: nextOrder + index }),
      );
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Photos</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={upload.isPending}
        >
          <Plus className="h-4 w-4 mr-2" />
          {upload.isPending ? "Uploading..." : "Add Photo"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(event) => handleFiles(event.target.files)}
          data-testid="vehicle-photo-input"
        />
      </div>

      {images.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed p-8 text-center">
          <ImageIcon className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No photos yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] gap-3 sm:grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] xl:grid-cols-[repeat(auto-fill,minmax(11rem,1fr))]">
          {images.map((image) => (
            <div
              key={image.id}
              className="group relative aspect-square max-w-[14rem] overflow-hidden rounded-lg border bg-muted"
              data-testid={`vehicle-photo-${image.id}`}
            >
              <img
                src={image.imageURL}
                alt=""
                loading="lazy"
                className="h-full w-full cursor-pointer object-cover transition-opacity hover:opacity-90"
                onClick={() => setSelected(image.imageURL)}
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute right-2 top-2 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                onClick={() => remove.mutate(image)}
                disabled={remove.isPending}
                aria-label="Delete photo"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={() => setSelected(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={selected}
            alt=""
            className="max-h-[90vh] max-w-full rounded-lg object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
